/**
 * Public registration API — used by the marketing-site /register pages.
 *
 * Uses plain `fetch` instead of the authenticated `useApi()` hook because the
 * public endpoints must NOT carry any bearer token (and the apiResponseWrapper
 * is intentionally a passthrough for /api/public/*).
 */
import type {
  CompleteRegistrationInput,
  CompleteRegistrationResponse,
  RegistrationStartInput,
  RegistrationStartResponse,
  RegistrationVerifyInput,
  RegistrationVerifyResponse,
  EulaVersionDto,
  SubscriptionTierDto,
} from '@shared/types/registration'

const BASE = '/api/public/registration'
const EULA_BASE = '/api/eula'
const TIERS_BASE = '/api/subscriptions/tiers'

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>
  if (!res.ok) {
    const message =
      typeof json['error'] === 'string' ? (json['error'] as string) : `Request failed (${res.status})`
    const err = new Error(message) as Error & { code?: string; status?: number }
    if (typeof json['code'] === 'string') err.code = json['code'] as string
    err.status = res.status
    throw err
  }
  return json as T
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path)
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>
  if (!res.ok) {
    const message =
      typeof json['error'] === 'string' ? (json['error'] as string) : `Request failed (${res.status})`
    const err = new Error(message) as Error & { code?: string; status?: number }
    if (typeof json['code'] === 'string') err.code = json['code'] as string
    err.status = res.status
    throw err
  }
  return json as T
}

export const publicRegistrationApi = {
  start: (input: RegistrationStartInput): Promise<RegistrationStartResponse> =>
    post(`${BASE}/start`, input),
  verify: (input: RegistrationVerifyInput): Promise<RegistrationVerifyResponse> =>
    post(`${BASE}/verify`, input),
  complete: (input: CompleteRegistrationInput): Promise<CompleteRegistrationResponse> =>
    post(`${BASE}/complete`, input),
  getEulaForRegion: (region: string): Promise<EulaVersionDto> =>
    get(`${EULA_BASE}/current?region=${encodeURIComponent(region)}`),
  listTiers: (): Promise<SubscriptionTierDto[]> => get(TIERS_BASE),
}
